import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Coins, Plus, Search, User, Wallet, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/theme-toggle";
import { useAuth } from "@/hooks/use-auth";
import { useBalance, formatAzn, WHATSAPP_LINK } from "@/lib/balance";
import { UserMenu } from "@/components/user-menu";
import { SideDrawer } from "@/components/side-drawer";
import logoAsset from "@/assets/albazar-logo.svg.asset.json";

const navLinks = [
  { to: "/elanlar", label: "Elanlar" },
  { to: "/magazalar", label: "Mağazalar" },
  { to: "/is-elanlari", label: "İş elanları" },
  { to: "/reklam", label: "Reklam yerləşdir" },
  { to: "/profil", label: "Profil" },
  { to: "/haqqimizda", label: "Haqqımızda" },
  { to: "/elaqe", label: "Əlaqə" },
];

export function SiteHeader() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const { user, loading } = useAuth();
  const { data: balance } = useBalance(user?.id);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate({ to: "/elanlar", search: { q, kateqoriya: "", seher: "", sirala: "yeni" } });
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border/70 bg-background/85 backdrop-blur-xl">
      <div className="container-x flex h-14 items-center gap-2 md:h-16 md:gap-3">
        <div className="order-last">
          <SideDrawer />
        </div>

        <Link
          to="/"
          className="flex min-w-0 shrink items-center bg-card sm:shrink-0"
          aria-label="Albazar.az ana səhifə"
        >
          <img
            src={logoAsset.url}
            alt="Albazar.az"
            width={1695}
            height={325}
            className="block h-auto w-[clamp(7rem,34vw,12rem)] max-w-full object-contain object-center sm:w-48 md:w-52"
          />
        </Link>

        {user && (
          <Link
            to="/balans"
            aria-label="Balans"
            className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-card px-2 py-1 text-xs font-bold sm:hidden"
          >
            <Coins className="size-3.5 text-teal" />
            <span className="text-teal">{formatAzn(balance ?? 0).replace("AZN", "₼")}</span>
          </Link>
        )}

        <form onSubmit={submit} className="relative hidden flex-1 md:block">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Elanlarda axtar..."
            className="h-11 rounded-full pl-9 pr-32"
            aria-label="Axtarış"
          />
          <Button
            type="submit"
            size="sm"
            className="absolute right-1 top-1/2 h-9 min-w-[108px] -translate-y-1/2 shrink-0 whitespace-nowrap rounded-full px-5 font-bold"
          >
            <Search className="size-4" /> Axtar
          </Button>
        </form>

        <nav aria-label="Əsas naviqasiya" className="ml-auto hidden items-center gap-1 lg:flex">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-1 lg:ml-0">
          {user && (
            <>
              <Link
                to="/balans"
                className="hidden shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-card px-3 py-1.5 text-sm font-bold sm:flex"
                aria-label="Balans"
              >
                <Coins className="size-4 text-teal" />
                <span className="text-teal">{formatAzn(balance ?? 0).replace("AZN", "₼")}</span>
              </Link>
              <Link to="/balans" className="hidden lg:block" aria-label="Balans göndər">
                <Button variant="outline" size="sm" className="rounded-full md:h-10 md:px-4">
                  <Send className="size-4" /> Balans göndər
                </Button>
              </Link>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="hidden lg:block"
                aria-label="Balans artır"
              >
                <Button
                  size="sm"
                  className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 md:h-10 md:px-4"
                >
                  <Wallet className="size-4" /> Balans artır
                </Button>
              </a>
            </>
          )}
          <ThemeToggle />
          {!loading &&
            (user ? (
              <UserMenu user={user} />
            ) : (
              <Button
                asChild
                variant="outline"
                size="sm"
                className="rounded-full md:h-10 md:px-4"
              >
                <Link to="/giris" aria-label="Giriş">
                  <User className="size-4" /> <span className="hidden sm:inline">Giriş</span>
                </Link>
              </Button>
            ))}
          <Link
            to="/yeni-elan"
            search={{}}
            aria-label="Elan yerləşdir"
            className="grid size-9 shrink-0 place-items-center rounded-full bg-info text-white shadow-glow transition-transform hover:scale-105 md:size-10"
          >
            <Plus className="size-5" strokeWidth={3} />
          </Link>
        </div>
      </div>
    </header>
  );
}